import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { filter, switchMap, map, catchError } from "rxjs/operators";
import { of, concat } from "rxjs";
import { WorkflowApi, Workflow, WorkflowsResponse } from "@/api/workflow.api";
import { RootEpic } from "../types";
import { startLoading, stopLoading } from "../loading";

interface WorkflowState {
  workflows: Workflow[];
  documentId: string | null;
  loading: boolean;
  error: string | null;
  total: number;
}

const initialState: WorkflowState = {
  workflows: [],
  documentId: null,
  loading: false,
  error: null,
  total: 0,
};

const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    getWorkflowsRequest: (
      state,
      _action: PayloadAction<{
        documentId: string;
        limit?: number;
        offset?: number;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getWorkflowsSuccess: (
      state,
      action: PayloadAction<WorkflowsResponse>
    ) => {
      state.loading = false;
      state.workflows = action.payload.workflows;
      state.documentId = action.payload.document_id;
      state.total = action.payload.total;
    },
    getWorkflowsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearWorkflows: (state) => {
      state.workflows = [];
      state.documentId = null;
      state.error = null;
      state.total = 0;
    },
  },
});

// Epic for getting workflows
export const getWorkflowsEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(workflowActions.getWorkflowsRequest.match),
    switchMap((action) => {
      const { documentId, limit = 100, offset = 0 } = action.payload;

      return concat(
        WorkflowApi.getWorkflowsByDocumentId(documentId, limit, offset).pipe(
          map((response) =>
            workflowActions.getWorkflowsSuccess(response)
          ),
          catchError((error) => {
            console.error("Error fetching workflows:", error);
            return of(
              workflowActions.getWorkflowsFailure(
                error?.message || "Failed to fetch workflows"
              )
            );
          })
        ),
      );
    })
  );

export const workflowEpics = [getWorkflowsEpic];

export const workflowActions = workflowSlice.actions;
export const workflowReducer = workflowSlice.reducer;

// Selectors
export const selectWorkflows = (state: any) => state.workflow.workflows;
export const selectWorkflowLoading = (state: any) => state.workflow.loading;
export const selectWorkflowError = (state: any) => state.workflow.error;
export const selectWorkflowTotal = (state: any) => state.workflow.total;
